import { Divider } from 'antd'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import PerFlexRow from '../../../../components/custom_components/FlexRow'
import { LoadingAndErrors } from '../../../../components/custom_components/LoadingAndErrors'
import MeetingBackground from '../../../../components/custom_components/Meeting/MeetingBackground'
import PerText from '../../../../components/custom_components/Text'
import { useCurrency } from '../../../../context/currency-context'
import useManagerMeetingStepTargets from '../../../../graphql/appraisal-flow/hooks/useManagerMeetingStepTargets'
import { useCurrentContractWithCareerPathEvaluation } from '../../../../graphql/contract/hooks/useCurrentContractWithCareerPathEvaluation'
import { CountrySettingsFragment, Currency, GovernmentJobTitle, MeetingStepStatus, MeetingStepViewId } from '../../../../types'
import { getFlexibleBonusUnknownCurrencyForConversion, toEligibleFlexibleBonusOverride } from '../shared/flexibleBonusUtil'
import { AppraisalContractHistory } from '../../contract/ContractHistory'
import { CurrentContractOverview } from '../../contract/CurrentContractComponent'
import { CurrentPendingContractChanges } from '../../contract/CurrentPendingContractChangesComponent'
import { useFindBonusByEmployeeIdAndMeetingId } from '../shared/appraisal-components/useBonusAmountEditableIsEnabled'
import { MeetingStepDefaultProps } from '../shared/appraisal-types'
import { AppraisalFlowHelper } from '../shared/AppraisalFlowHelper'
import { calculateBonusWithSalary } from '../shared/targetBonusCalculation-util'
import { useContractWithNewTargetsValidator } from './util/useContractWithNewTargetsValidator'
import useEmployeesCurrentContractChanges from '../../../../graphql/contract/hooks/useEmployeesCurrentContractChanges'
import moment from 'moment-timezone'
import { useGovernmentJobTitlesByHiringOfficeId } from '../../../../graphql/government-job-title/hooks/useGovernmentJobTitlesByHiringOfficeId'
import { retrieveContractStartAndEndDateFromContract } from '../../../profile/contract/utils/contractUtil'

interface AppraisalMeetingStepContractChangesProps extends MeetingStepDefaultProps {
  countrySettings: CountrySettingsFragment
}

const AppraisalMeetingStepContractChanges: FC<AppraisalMeetingStepContractChangesProps> = ({
  backBtn,
  nextBtn,
  meeting,
  countrySettings,
}) => {
  const { data, loading, error } = useManagerMeetingStepTargets(
    meeting.employee.id,
    meeting.id,
    meeting.targetsCalculationHelper.newTargetsReferenceYear,
    meeting.employee.currentContract!.legalEntity.country,
    true,
  )

  if (loading || error || !data || !data.currentContract) {
    return <LoadingAndErrors isLoading={loading} hasNoData={!data || !data.currentContract} error={error} />
  } else {
    return (
      <AppraisalMeetingStepContractChangesContent meeting={meeting} countrySettings={countrySettings} backBtn={backBtn} nextBtn={nextBtn} />
    )
  }
}

const AppraisalMeetingStepContractChangesContent: FC<MeetingStepDefaultProps & AppraisalMeetingStepContractChangesProps> = ({
  backBtn,
  nextBtn,
  meeting,
  countrySettings,
}) => {
  const [t] = useTranslation()
  const [existingUnknownGovernmentJobTitle, toggleExistingUnknownGovernmentJobTitle] = useState(false)
  const { currencyConversionData } = useCurrency()

  const { newTargetsReferenceYear } = meeting.targetsCalculationHelper

  const {
    data: findBonusAmountData,
    error: findBonusAmountDataError,
    loading: findBonusAmountLoading,
  } = useFindBonusByEmployeeIdAndMeetingId(meeting.employee.id, meeting.id)

  const currentYearFlexibleBonus = findBonusAmountData?.findBonusByEmployeeIdInAppraisalSeasonOfMeeting
  const flexibleBonusUnknownCurrency = getFlexibleBonusUnknownCurrencyForConversion(
    currentYearFlexibleBonus,
    meeting.employee.currentContract!.currencyForPayments as Currency,
    currencyConversionData,
  )

  useContractWithNewTargetsValidator(
    currentYearFlexibleBonus,
    countrySettings.hasGovernmentJobTitleReferenceLevel,
    flexibleBonusUnknownCurrency,
  )

  const { loading, error, data } = useCurrentContractWithCareerPathEvaluation(
    meeting.employee.id,
    newTargetsReferenceYear,
    true,
    true,
    // Bad practice but in this case currentContract will always exist since you can't have appraisal with employee who doesn't have contract
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    meeting.employee.currentContract!.legalEntity.country,
  )

  const {
    loading: loadingContractChanges,
    error: errorContractChanges,
    data: dataContractChanges,
  } = useEmployeesCurrentContractChanges(meeting.employee.id)

  const [getGovernmentJobTitles, { data: gjtData, loading: gjtLoading, error: gjtError }] = useGovernmentJobTitlesByHiringOfficeId()

  useEffect(() => {
    if (
      !!data &&
      !!data.currentContract &&
      !!data.currentContract.contractGovernmentJobTitle &&
      countrySettings.hasGovernmentJobTitleReferenceLevel
    ) {
      getGovernmentJobTitles({
        variables: {
          hiringOfficeId: data.currentContract.contractGovernmentJobTitle.governmentJobTitle.hiringOffice.id,
          excludePartnerLevels: true,
        },
      })
    }
  }, [getGovernmentJobTitles, countrySettings, data])

  if (
    loading ||
    gjtLoading ||
    loadingContractChanges ||
    error ||
    gjtError ||
    errorContractChanges ||
    !data ||
    !dataContractChanges ||
    !data.currentContract ||
    !data.careerPath ||
    !dataContractChanges.contractChangesByEmployee ||
    findBonusAmountDataError ||
    !findBonusAmountData
  ) {
    return (
      <LoadingAndErrors
        isLoading={loading || findBonusAmountLoading || loadingContractChanges || gjtLoading}
        hasNoData={!data || !data.currentContract || !dataContractChanges || !dataContractChanges.contractChangesByEmployee}
        error={error || findBonusAmountDataError || errorContractChanges || gjtError}
      />
    )
  }

  const cannotProceed =
    (meeting.requiresSpecialHandlingOfLowTag && dataContractChanges.contractChangesByEmployee.length > 0) ||
    existingUnknownGovernmentJobTitle

  const currentAnnualBonus = data.currentContract.targetBonus ? data.currentContract.targetBonus.amount : 0

  const [contractStartDate, contractEndDate] = retrieveContractStartAndEndDateFromContract(data.currentContract)
  const flexibleBonusOverride = toEligibleFlexibleBonusOverride(
    currentYearFlexibleBonus,
    data.currentContract.currencyForPayments,
    currencyConversionData,
    data.branchOfficeByEmployee.timezone,
    newTargetsReferenceYear,
    data.contractsInYear,
    data.targetBonusOfYear,
    data.contractChangesByEmployee,
  )
  const prorataCalc = calculateBonusWithSalary(
    data.branchOfficeByEmployee.timezone,
    data.isBonusValidationEnabled,
    newTargetsReferenceYear,
    data.contractChangesByEmployee,
    data.targetBonusOfYear,
    data.salaryOfYear,
    data.currentContract.annualSalary ? data.currentContract.annualSalary.amount : 0,
    currentAnnualBonus,
    currencyConversionData,
    data.currentContract.currencyForPayments,
    contractStartDate,
    contractEndDate,
    data.contractsInYear,
    flexibleBonusOverride,
  )

  const targetBonusByChanges =
    data.contractChangesByEmployee &&
    data.contractChangesByEmployee.find((cc) => {
      const changes = JSON.parse(cc.changes)
      const targetBonus = changes.bonus
      return targetBonus !== undefined
    })

  const yearOfChanges =
    targetBonusByChanges && targetBonusByChanges.validFrom ? moment.utc(targetBonusByChanges.validFrom).year() : undefined

  const effectiveBonusAmount = prorataCalc.flexibleBonusAmount ?? prorataCalc.amount

  const newBonus = {
    effectiveBonusAmount: effectiveBonusAmount,
    annualAmount:
      targetBonusByChanges && yearOfChanges && [newTargetsReferenceYear, newTargetsReferenceYear - 1].includes(yearOfChanges)
        ? JSON.parse(targetBonusByChanges.changes).bonus
        : currentAnnualBonus,
    prorata: prorataCalc.amount,
    prorataPartials: prorataCalc.partials,
    currency: data.currentContract.currencyForPayments,
    employeeTimezone: data.branchOfficeByEmployee.timezone,
  }

  const currentStepStatus = cannotProceed ? MeetingStepStatus.Incomplete : MeetingStepStatus.Done
  const stepTooltip = cannotProceed
    ? existingUnknownGovernmentJobTitle
      ? t('appraisalMeeting.stepConclusion.disabledButtonLabel.invalidGovernmentJobTitle')
      : t('appraisalMeeting.stepConclusion.disabledButtonLabel.existingContractChangesForLowEmployee')
    : ''

  return (
    <div data-e2e-appraisal-step-contract-changes>
      <MeetingBackground>
        <PerText appearance="heading" mb="m" color="primary">
          {t('appraisalMeeting.stepContractChanges.pageHeaderMeeting')}
        </PerText>
        <PerText color="secondary" mb="m">
          {t('appraisalMeeting.stepContractChanges.pageDescriptionMeeting')}
        </PerText>
        <CurrentContractOverview
          employeeTimezone={meeting.employee.branchOffice.timezone}
          referenceYear={newTargetsReferenceYear}
          newBonus={newBonus}
          newTargetsAllowed={meeting.allowedMonetaryTargets.newTargetsAllowed}
          currentContract={data.currentContract}
          careerPathEvaluation={data.careerPathEvaluation}
          careerPathData={data.careerPath}
        />
        <AppraisalContractHistory
          employeeId={meeting.employee.id}
          employeeTimezone={meeting.employee.branchOffice.timezone}
          isMonthlySalaryDisplayed={data.currentContract.isMonthlySalaryDisplayed}
          monthlySalaryMonthsInYear={data.currentContract.monthlySalaryMonthsInYear}
        />
        <Divider />
        <CurrentPendingContractChanges
          meeting={meeting}
          currentContract={data.currentContract}
          careerPathEvaluation={data.careerPathEvaluation ? data.careerPathEvaluation : null}
          careerPathData={data.careerPath}
          toggleExistingUnknownGovernmentJobTitle={toggleExistingUnknownGovernmentJobTitle}
          countrySettings={countrySettings}
          governmentJobTitles={
            !!gjtData && !!gjtData.governmentJobTitlesByHiringOfficeId
              ? (gjtData.governmentJobTitlesByHiringOfficeId as GovernmentJobTitle[])
              : undefined
          }
        />
      </MeetingBackground>
      <PerFlexRow justify="space-between">
        {backBtn && backBtn(currentStepStatus)}
        {nextBtn && nextBtn(currentStepStatus, false, stepTooltip)}
      </PerFlexRow>
      <AppraisalFlowHelper stepViewId={MeetingStepViewId.ContractChanges} stepStatus={currentStepStatus} />
    </div>
  )
}

export default AppraisalMeetingStepContractChanges
